import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Package, Truck, Home, CheckCircle2, XCircle, Clock } from 'lucide-react';
import api, { formatPrice } from '../api';

const STEPS = [
  { id: 'placed', label: 'Order placed', desc: 'Received by our workshop', icon: Package },
  { id: 'shipped', label: 'Shipped', desc: 'On its way to you', icon: Truck },
  { id: 'delivered', label: 'Delivered', desc: 'Arrived at your door', icon: Home },
];
const RANK = { pending_payment: 0, placed: 1, shipped: 2, delivered: 3 };

function Timeline({ order }) {
  const history = Object.fromEntries((order.status_history || []).map(h => [h.status, h.at]));
  if (order.status === 'cancelled') {
    return (
      <div data-testid="track-cancelled" className="flex items-center gap-4 border border-black/10 p-6">
        <XCircle className="w-8 h-8 text-[#1a1a1a]/50" />
        <div>
          <p className="font-serif-display text-2xl">This order was cancelled.</p>
          <p className="text-sm text-[#1a1a1a]/60">If you paid online, your refund will arrive within 5–7 working days.</p>
        </div>
      </div>
    );
  }
  const rank = RANK[order.status] ?? 1;
  return (
    <ol data-testid="track-timeline" className="relative">
      {order.status === 'pending_payment' && (
        <li className="flex gap-5 pb-8"><Clock className="w-8 h-8 text-[#C6410E]" /><div><p className="font-medium">Awaiting payment</p><p className="text-sm text-[#1a1a1a]/60">Complete your payment to confirm this order.</p></div></li>
      )}
      {STEPS.map((s, i) => {
        const done = rank >= i + 1;
        const current = rank === i + 1;
        const Icon = s.icon;
        return (
          <li key={s.id} data-testid={`track-step-${s.id}`} data-state={done ? (current ? 'current' : 'done') : 'upcoming'} className="flex gap-5 pb-8 last:pb-0 relative">
            {i < STEPS.length - 1 && <span className={`absolute left-4 top-9 bottom-0 w-px ${rank > i + 1 ? 'bg-[#C6410E]' : 'bg-black/15'}`} />}
            <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 border transition-colors ${done ? 'bg-[#C6410E] border-[#C6410E] text-[#F4EEE4]' : 'border-black/20 text-[#1a1a1a]/40'}`}>
              {done && !current ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
            </span>
            <div className="pt-1">
              <p className={`font-medium ${done ? '' : 'text-[#1a1a1a]/45'}`}>{s.label}{current && <span className="ml-2 text-[10px] tracking-widest-plus uppercase text-[#C6410E]">Current</span>}</p>
              <p className="text-sm text-[#1a1a1a]/60">{history[s.id] ? new Date(history[s.id]).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : s.desc}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function TrackOrder() {
  const { state } = useLocation();
  const [form, setForm] = useState({ order_id: state?.order_id || '', email: state?.email || '' });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setOrder(null);
    try {
      const { data } = await api.post('/orders/track', form);
      setOrder(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <main className="max-w-5xl mx-auto px-6 py-20">
      <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-4">Order tracking</p>
      <h1 className="font-serif-display text-5xl md:text-6xl mb-4">Where is my order?</h1>
      <p className="text-[#1a1a1a]/70 max-w-xl mb-12">Enter your order number and the email you used at checkout. No account needed.</p>

      <form onSubmit={submit} data-testid="track-form" className="grid md:grid-cols-[1fr_1fr_auto] gap-6 items-end mb-14">
        <div>
          <label className="text-[11px] tracking-widest-plus uppercase block mb-2">Order number</label>
          <input data-testid="track-order-input" required value={form.order_id} onChange={e => setForm({ ...form, order_id: e.target.value })} placeholder="ord_xxxxxxxxxx" className="w-full bg-transparent border-b border-black/30 focus:border-[#C6410E] outline-none py-2 text-sm font-mono" />
        </div>
        <div>
          <label className="text-[11px] tracking-widest-plus uppercase block mb-2">Email</label>
          <input data-testid="track-email-input" required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="w-full bg-transparent border-b border-black/30 focus:border-[#C6410E] outline-none py-2 text-sm" />
        </div>
        <button data-testid="track-submit-btn" disabled={busy} className="btn-primary">{busy ? 'Looking…' : 'Track order'}</button>
      </form>

      {error && <p data-testid="track-error" className="text-sm text-[#C6410E] border-l-2 border-[#C6410E] pl-4 mb-10">{error}</p>}

      {order && (
        <div data-testid="track-result" className="grid md:grid-cols-5 gap-12 fade-up">
          <div className="md:col-span-3">
            <div className="flex items-baseline justify-between mb-8">
              <div>
                <p className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/60 mb-1">Order</p>
                <p data-testid="track-order-id" className="font-serif-display text-3xl">{order.id}</p>
              </div>
              <span data-testid="track-status" className="text-[11px] tracking-widest-plus uppercase bg-[#E9E1D4] px-3 py-1">{order.status.replace('_', ' ')}</span>
            </div>
            <Timeline order={order} />
          </div>
          <aside className="md:col-span-2 bg-[#E9E1D4] p-6 h-fit">
            <p className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/60 mb-4">Summary</p>
            <ul className="text-sm space-y-2 mb-4">
              {order.items.map((it, i) => <li key={i} className="flex justify-between gap-3"><span>{it.name} × {it.qty}</span><span>{formatPrice(it.price * it.qty)}</span></li>)}
            </ul>
            <div className="border-t border-black/10 pt-3 text-sm space-y-1">
              <div className="flex justify-between"><span>Shipping</span><span>{order.shipping_fee ? formatPrice(order.shipping_fee) : 'Free'}</span></div>
              <div className="flex justify-between font-medium text-base"><span>Total</span><span>{formatPrice(order.total)}</span></div>
              <p className="text-xs text-[#1a1a1a]/60 pt-2">{order.payment_status === 'paid' ? 'Paid online' : order.payment_status === 'pending' ? 'Payment pending' : 'Cash on Delivery'}</p>
            </div>
            <p className="text-xs text-[#1a1a1a]/60 mt-5">Delivering to {order.shipping.name}, {order.shipping.city} — {order.shipping.pincode}</p>
            <p className="text-xs text-[#1a1a1a]/60 mt-1">Placed {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </aside>
        </div>
      )}

      <p className="text-sm text-[#1a1a1a]/60 mt-16">Questions about your order? <Link to="/contact" className="text-[#C6410E] link-underline">Write to us</Link>.</p>
    </main>
  );
}
